import axios from 'axios';
import qs from 'query-string';
import FileDownload from 'js-file-download';

import { useAppContext } from '../providers/AppProvider';
import { useAuthContext } from '../providers/AuthProvider';
import { useFirebaseContext } from '../providers/FirebaseProvider';

const hydraApi = axios.create({
  baseURL: process.env.REACT_APP_HYDRA_URL,
});

const mapPolicyIds = (policies) =>
  policies.map((policy) => ({
    id: policy.id,
    indicationId: policy.indicationId,
    productId: policy.productId,
  }));

const mapDocumentLinksToRequests = (documentData, authToken) => {
  const docNames = documentData.map((d) => d.DocumentName);
  const requests = documentData.map(({ DocumentId }) =>
    hydraApi.get(`/document/${DocumentId}?authToken=${authToken}`, {
      responseType: 'blob',
    })
  );
  return { docNames, requests };
};

export const usePolicyApi = () => {
  const { user } = useAuthContext();
  const { setGlobalError } = useAppContext();
  const firebase = useFirebaseContext();

  const fetchPolicyLinks = async (indication, selections, productName) => {
    try {
      const authToken = await firebase.auth().currentUser.getIdToken();
      const formularyId = selections.map((plan) => plan.formularyId);
      const queryString = qs.stringify({
        formularyId,
        indication,
        authToken,
        productName,
      });
      const { data: res } = await hydraApi.get('/priorauth?' + queryString);

      const mappedResults = selections.map((selection) => {
        const match = res.data.find(
          (r) => selection.formularyId === parseInt(r.formularyId)
        );
        if (!match) return selection;
        return { ...selection, docs: match.data };
      });
      return mappedResults;
    } catch (err) {
      console.log(err);
      setGlobalError({
        err: err,
        message: 'The server responded with a error, please try again later.',
      });
    }
  };

  const downloadPolicyDocuments = async (documentData) => {
    const authToken = await firebase.auth().currentUser.getIdToken();
    const { docNames, requests } = mapDocumentLinksToRequests(
      documentData,
      authToken
    );
    const responses = await axios.all(requests);
    const docs = responses.map(({ data }, index) => ({
      data,
      name: docNames[index],
    }));
    docs.forEach((d) => FileDownload(d.data, `${d.name}.pdf`));
  };

  const updateFavoritePolicies = async (policies) => {
    const mappedPolicies = mapPolicyIds(policies);
    return await firebase.firestore
      .collection('users')
      .doc(user.uid)
      .set({ policiesShownOnHomepage: mappedPolicies }, { merge: true });
  };

  const updateVisiblePolicies = async (policies) => {
    const mappedPolicies = mapPolicyIds(policies);
    return await firebase.firestore
      .collection('practices')
      .doc(user.practiceId)
      .set({ policies: mappedPolicies }, { merge: true });
  };

  return {
    downloadPolicyDocuments,
    fetchPolicyLinks,
    updateFavoritePolicies,
    updateVisiblePolicies,
  };
};
